import { withDependencies, named } from '@wix/thunderbolt-ioc'
import type { IViewerManagerImplementor, ViewerAPI } from 'thunderbolt-viewer-manager-types'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import type { DataMapName, Data, BaseDataItem, QueryProperty, RepeaterDataItem } from '@wix/thunderbolt-becky-types'
import { PageJson, pointers } from '@wix/thunderbolt-becky-types'
import {
	CarmiInstance,
	ILanguage,
	LanguageSymbol,
	EditorFeatureConfigSymbol,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import { getFullId, isDisplayedOnly, getItemId, getDisplayedId } from '@wix/thunderbolt-commons'
import { name } from './symbols'
import type { ViewerManagerDataApiEditorConfig } from './types'

export const ViewerManagerDataApi = withDependencies(
	[named(EditorFeatureConfigSymbol, name), CarmiInstance, LanguageSymbol, ViewerManagerStructureAPISymbol],
	(
		{
			data,
			repeaterAncestor: repeaterAncestorMap,
			queryIdToRepeatedCompId,
			dataQueryIdToCompId,
		}: ViewerManagerDataApiEditorConfig,
		{ data: dataApi }: DSCarmi,
		languageApi: ILanguage,
		structureViewerApi: ViewerAPI['structure']
	): IViewerManagerImplementor<'data'> => {
		const getData = (type: DataMapName, id: string) => data[type]?.[id]

		// in case of repeaters, return fixed data that imitate repeater data items for dm
		const getTemplateData = (type: DataMapName, queryId: string) => {
			if (!isDisplayedOnly(queryId)) {
				return
			}
			const fullQueryId = getFullId(queryId)
			const itemId = getItemId(queryId)
			const fullCompId = fullQueryId.includes(`-${pointers.MAP_NAME_TO_QUERY[type as QueryProperty]}`)
				? fullQueryId.split(`-${pointers.MAP_NAME_TO_QUERY[type as QueryProperty]}`)[0] // for byref item in repeater, compId is inside inflated queryId
				: queryIdToRepeatedCompId[fullQueryId]

			const repeaterAncestor = structureViewerApi.getStructure((repeaterAncestorMap[fullCompId] || '') as string)
			const repeaterAncestorItems = (data.document_data[repeaterAncestor?.dataQuery || ''] as RepeaterDataItem)
				?.items

			// only if this item exists in the ancestor repeater
			if (!repeaterAncestorItems?.find((item) => item === itemId)) {
				return
			}

			const dataItem = getData(type, fullQueryId)
			if (dataItem) {
				return {
					...dataItem,
					id: queryId,
				}
			}
		}

		const translations: Partial<PageJson['translations']> = {}

		return {
			data: {
				getData: (type: DataMapName, id: string): BaseDataItem =>
					getData(type, id) ?? getTemplateData(type, id),
				updateData: <T extends DataMapName>(type: T, id: string, dataItem: Data[T][string]) => {
					dataApi.setData(type, id, dataItem)
				},
				// TODO deprecate this api if we can. since byrefsis we return undefined for inflated data items.
				getTranslationDataItem: (id: string) => {
					const currentLanguageData = translations[languageApi.userLanguage]
					return currentLanguageData?.data.document_data[id]! // TODO fix type in dm to allow returning undefined
				},
				updateTranslationDataItem: (id: string, dataItem: BaseDataItem) => {
					const [, lang, dataItemId] = id.split('^') as [string, ILanguage['userLanguage'], string]

					translations[lang] = translations[lang] || { data: { document_data: {} } }
					translations[lang]!.data.document_data[dataItemId] = dataItem

					dataApi.updateTranslationDataItem(lang, dataItemId, dataItem)
				},
				getCompIdByDataQueryId: (dataQueryId: string) => {
					if (isDisplayedOnly(dataQueryId)) {
						const templateDataId = getFullId(dataQueryId)
						const templateCompId = dataQueryIdToCompId[templateDataId]
						if (templateCompId) {
							return getDisplayedId(templateCompId, getItemId(dataQueryId))
						}
					}
					return dataQueryIdToCompId[dataQueryId]
				},
			},
		}
	}
)
